import paragraphLookup from 'bellmere/validations/document/paragraphLookup';
import rateTextSegments from 'bellmere/validations/document/rateTextSegments';
import sentenceLookup from 'bellmere/validations/document/sentenceLookup';
import { TextSegment } from 'bellmere/validations/document/TextSegment.d';
import { spanTypes } from 'bellmere/validations/document/ElementTypes';

import {
  Annotation,
  AnnotatorObject,
  CategoryClassName,
  CommentHelper,
  DisplayType,
  CommentCategory,
  CommentInputAnnotation,
  FeedbackData
} from './annotator-types';
import handleBareTextNode from './bare-text-node-handler';

declare const Annotator: AnnotatorObject;

// Convert a string to camel case
export const strToCamelCase = (str: string): string => (
  str?.replace(/(?:^\w|[A-Z]|\b\w)/g, (word) => word.toUpperCase()).replace(/\s+/g, '')
);

// Convert snake case to camel case
export const snakeToCamelCase = (str: string): string => (
  str?.replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''))
);

// Convert snake case to pascal case
export const snakeToPascalCase = (str: string): string | null => {
  if (!str) return null;

  const camelCase = snakeToCamelCase(str);
  return camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
};

// Convert a string to camel case
export const camelCase = (str: string | undefined): string | null => {
  if (!str) return null;

  return str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word) => word.toUpperCase()).replace(/\s+/g, '');
};

export const pascalCase = (str: string | undefined): string | null => {
  if (!str) return null;

  return (str.match(/[a-zA-Z0-9]+/g) || []).map((w: string) => `${w.charAt(0).toUpperCase()}${w.slice(1)}`).join('');
};

// Resolve the category name for an annotation
export const resolveCategory = (annotation: Annotation): string | null => {
  if (annotation.commentCategory === CommentCategory.All) return 'All';
  if (annotation.commentCategory === CommentCategory.CriticalThinking) return 'CriticalThinking';
  if (Array.isArray(annotation.sources)) return 'UseOfSources';

  return pascalCase(annotation.commentCategory);
};

export const capitalize = (label?: string): string | null => {
  if (!label) return null;

  const uppercaseLabel = label.replace(/_/g, ' ');
  return uppercaseLabel.charAt(0).toUpperCase() + uppercaseLabel.slice(1);
};

export const titleize = (label?: string): string | null => {
  if (!label) return null;

  const humanizedLabel = capitalize(label);
  if (!humanizedLabel) return null;

  return humanizedLabel.split(' ').map((word) => capitalize(word)).join(' ');
};

export const commentHelperType = (annotation: CommentInputAnnotation): string | null => {
  const commentHelper = Annotator.data.commentHelpers?.find((a) => a.id === annotation?.commentHelperId);

  if (annotation?.hideCommentType || annotation?.hide_comment_type || commentHelper?.hide_comment_type) {
    return null;
  }

  let commentType = commentHelper?.comment_type || annotation?.commentType || annotation?.comment_type;
  if (!commentType) {
    commentType = (
      annotation?.sources ? CommentCategory.UseOfSources : CommentCategory.General
    ).toLowerCase().replace(/_/g, ' ');
    commentType = commentType[0].toUpperCase() + commentType.slice(1);
  }
  return commentType;
};

export const matchTimestamp = (timestamp: string): string | null => {
  const matches = timestamp.match(/(paragraph-\d+|\b\d{13}\b)/g);
  if (!matches) return null;

  return matches[0];
};

export const mergeParagraphAnnotations = (annotations: Annotation[]): Annotation[] => {
  const updatedAnnotations = annotations?.map((annotation) => {
    const newAnnotation = { ...annotation };
    newAnnotation.taskAligned = annotation.taskAligned || annotation.task_aligned;
    newAnnotation.highlightsText ||= $(newAnnotation.highlights).text();

    return newAnnotation;
  });

  const paragraphAnnotations = Annotator.data.annotations.
    filter((annotation: Annotation) => annotation.display_type === DisplayType.Paragraph).
    map((annotation: Annotation) => {
      const isCriticalThinking = annotation.category_name?.includes('Critical thinking');
      const newAnnotation = addParagraphHighlight(
        annotation.highlights_text,
        annotation.comment,
        annotation.comment_helper_id,
        annotation.comment_helper_ids,
        isCriticalThinking ?
          CommentCategory.CriticalThinking :
          CommentCategory[camelCase(annotation.category_name) || ''],
        annotation.comment_type,
        annotation.id?.toString() || '',
        annotation.comment_helpers,
        !!annotation.hide_comment_type,
        !!annotation.task_aligned,
        annotation.timestamp,
        annotation.category_name,
        annotation.paragraph_indices,
        annotation.sentences
      );

      // addParagraphHighlight returns null if fuzzy matching fails
      // This can happen when paragraph_indices/sentences are missing from database
      if (newAnnotation) {
        newAnnotation.bellmere = true;
      }

      return newAnnotation;
    }).
    filter((annotation) => annotation !== null);

  return updatedAnnotations.concat(paragraphAnnotations);
};

export const addParagraphHighlight = (
  highlightsText: string, comment: string, commentHelperId: number | undefined, commentHelperIds: number[] | undefined,
  commentCategory: string, commentType: string | undefined, id: string, commentHelpers: CommentHelper[] | undefined,
  hideCommentType: boolean, taskAligned: boolean, timestamp: string, categoryName: string | undefined,
  paragraphIndices: number[] | undefined, sentences: string[] | undefined
): Annotation | null => {
  const documentRoot = Annotator.highlighter.el;
  const isMultiParagraph = paragraphIndices && paragraphIndices.length >= 2;

  // For multi-paragraph annotations, use fuzzy matching with sentences
  if (isMultiParagraph) {
    // Get all text segments from the document for fuzzy matching
    // Use paragraphLookup instead of sentenceLookup because stored "sentences" are actually full paragraphs
    const allSegments = paragraphLookup(sentenceLookup(documentRoot));
    const blocks: HTMLElement[] = [];
    const matchedIndices: number[] = [];
    const matchedSentences: string[] = [];

    // Fuzzy match each sentence against all text segments in the document
    sentences?.forEach((sentenceToMatch, i) => {
      const expectedIndex = paragraphIndices[i];

      if (!sentenceToMatch) {
        return;
      }

      // Fuzzy match against all text segments
      const textSegment = rateTextSegments(allSegments, sentenceToMatch, 0.9, true)[0]?.item;

      if (!textSegment) {
        return;
      }

      // Find the paragraph element containing this matched text
      let closestBlock =
        textSegment.startElement.parentElement?.closest(`:not(${spanTypes.join(',')})`) as HTMLElement;

      if (!closestBlock) {
        return;
      }

      // Check for line breaks (same as single paragraph logic)
      closestBlock = checkForLineBreaks(closestBlock, textSegment);

      // Track matched paragraphs
      matchedIndices.push(expectedIndex); // Use expected index from API for multi-paragraph
      matchedSentences.push(sentenceToMatch);

      // Add highlighting to the block
      if (!closestBlock.classList.contains('paragraph-highlight')) {
        closestBlock.dataset.commentCategory = CategoryClassName[snakeToPascalCase(commentCategory) || -1];
      }
      closestBlock.classList.add('paragraph-highlight');

      // Set timestamp identifier - all paragraphs in multi-paragraph annotation share the same timestamp
      if (blocks.length === 0) {
        const firstParagraphId = `paragraph-${expectedIndex}`;
        const identifier = timestamp || `${firstParagraphId}-${id}`;
        closestBlock.dataset.timestamp = identifier;
      } else {
        // Use the same identifier as the first block
        const firstBlockTimestamp = blocks[0].dataset.timestamp;
        closestBlock.dataset.timestamp = firstBlockTimestamp;
      }

      // Store the paragraph index for later lookup
      closestBlock.dataset.paragraphIndex = expectedIndex.toString();

      // Keep all identifiers in a JSON array on the element so selectHighlight
      // can find it even when a later annotation overwrites data-timestamp
      const pendingIdentifier = timestamp || `paragraph-${paragraphIndices[0]}-${id}`;
      const existingIds: string[] = JSON.parse(closestBlock.dataset.highlightIdentifiers || '[]');
      if (!existingIds.includes(pendingIdentifier)) {
        existingIds.push(pendingIdentifier);
        closestBlock.dataset.highlightIdentifiers = JSON.stringify(existingIds);
      }

      blocks.push(closestBlock);
    });

    // Add icon to each matched paragraph
    if (blocks.length > 0) {
      const firstParagraphId = `paragraph-${matchedIndices[0]}`;
      const identifier = timestamp || `${firstParagraphId}-${id}`;

      blocks.forEach((block, index) => {
        const paragraphId = `paragraph-${matchedIndices[index]}`;

        let paragraphIconContainer = document.querySelector(`#${paragraphId}`);
        if (!paragraphIconContainer) {
          paragraphIconContainer = document.createElement('div');
          paragraphIconContainer.setAttribute('id', paragraphId);
          paragraphIconContainer.classList.add('paragraph-icon-container');
          block.appendChild(paragraphIconContainer);
        }

        const categoryIcon = document.createElement('div');
        categoryIcon.dataset.commentCategory = 'comment-category-task-and-content';
        categoryIcon.classList.add('paragraph-icon');

        const paragraphIndexForIcon = matchedIndices[index].toString();
        categoryIcon.addEventListener('click', ({ target }) => {
          const clickedCategory = (target as HTMLElement).dataset.commentCategory;
          Annotator.selectHighlight(`${identifier},${clickedCategory}`, true, paragraphIndexForIcon);
        });
        categoryIcon.id = `${identifier}-${index}`;
        paragraphIconContainer.appendChild(categoryIcon);
      });

      return {
        id,
        comment,
        commentHelperId,
        commentHelperIds,
        commentCategory,
        highlightsText,
        highlights_text: highlightsText,
        timestamp: identifier,
        commentType,
        display_type: DisplayType.Paragraph,
        hide_comment_type: hideCommentType,
        category_name: categoryName,
        commentHelpers,
        paragraph_indices: matchedIndices,
        sentences: matchedSentences
      } as unknown as Annotation;
    }

    return null;
  }

  // Single paragraph - use fuzzy text matching
  let closestBlock: HTMLElement | null = null;
  let paragraphId: string = 'unknown';
  const textSegment = rateTextSegments(
    paragraphLookup(sentenceLookup(documentRoot)),
    highlightsText, 0.9, true
  )[0]?.item;

  if (textSegment) {
    if (!textSegment.startElement) {
      return null;
    }

    closestBlock = textSegment.startElement.parentElement?.closest(`:not(${spanTypes.join(',')})`) as HTMLElement;

    // Handle bare text nodes (not wrapped in any paragraph)
    if (closestBlock === documentRoot) {
      const resolvedBlock = handleBareTextNode(
        textSegment.startElement,
        textSegment,
        documentRoot,
        highlightsText
      );

      if (!resolvedBlock) {
        return null;
      }

      closestBlock = resolvedBlock;
    }

    paragraphId = `paragraph-${textSegment.paragraphIndex}`;
  }

  if (closestBlock) {
    closestBlock = checkForLineBreaks(closestBlock, textSegment);

    if (!closestBlock.classList.contains('paragraph-highlight')) {
      closestBlock.dataset.commentCategory = CategoryClassName[snakeToPascalCase(commentCategory) || -1];
    }
    closestBlock.classList.add('paragraph-highlight');
    closestBlock.dataset.timestamp = paragraphId;
    // Store the paragraph index for later lookup
    closestBlock.dataset.paragraphIndex = textSegment.paragraphIndex.toString();

    let paragraphIconContainer = document.querySelector(`#${paragraphId}`);
    if (!paragraphIconContainer) {
      paragraphIconContainer = document.createElement('div');
      paragraphIconContainer.setAttribute('id', paragraphId);
      paragraphIconContainer.classList.add('paragraph-icon-container');
      closestBlock.appendChild(paragraphIconContainer);
    }

    const categoryIcon = document.createElement('div');
    categoryIcon.dataset.commentCategory = CategoryClassName[snakeToPascalCase(commentCategory) || -1];
    categoryIcon.classList.add('paragraph-icon');

    const identifier = timestamp || `${paragraphId}-${id}`;
    const paragraphIndexForIcon = textSegment.paragraphIndex.toString();
    categoryIcon.addEventListener('click', ({ target }) => {
      const clickedCategory = (target as HTMLElement).dataset.commentCategory;
      Annotator.selectHighlight(`${identifier},${clickedCategory}`, true, paragraphIndexForIcon);
    });
    categoryIcon.id = identifier;
    paragraphIconContainer.appendChild(categoryIcon);

    return {
      id,
      comment,
      commentHelperId,
      commentHelperIds,
      commentCategory,
      highlightsText,
      highlights_text: highlightsText,
      timestamp: identifier,
      commentType,
      display_type: DisplayType.Paragraph,
      hide_comment_type: hideCommentType,
      category_name: categoryName,
      commentHelpers,
      taskAligned
    } as unknown as Annotation;
  }

  return null;
};

const checkForLineBreaks = (block: HTMLElement, segment: TextSegment): HTMLElement => {
  let result = block;
  const nodes = Array.from(block.childNodes);
  const startIndex = nodes.findIndex((node) => node === segment.startElement);
  const endIndex = nodes.findIndex((node) => node === segment.endElement) + 1;
  const firstTextNodeIndex =
      nodes.findIndex((node) => node.nodeType === Node.TEXT_NODE && (node.textContent || '').trim().length > 0);
  const lastTextNodeIndex =
      nodes.findLastIndex((node) => node.nodeType === Node.TEXT_NODE && (node.textContent || '').trim().length > 0);

  if (firstTextNodeIndex >= 0 && startIndex > firstTextNodeIndex) {
    result = insertParagraph(result, nodes, startIndex, true);
  }
  if (endIndex >= 0 && lastTextNodeIndex > endIndex) {
    result = insertParagraph(result, nodes, endIndex, false);
  }

  // After splits, find which paragraph actually contains the matched text
  // by checking which element contains segment.startElement
  if (segment.startElement.parentElement) {
    const containingBlock = segment.startElement.parentElement.closest('p') as HTMLElement;
    if (containingBlock) {
      result = containingBlock;
    }
  }

  return result;
};

const insertParagraph = (block: HTMLElement, nodes: Node[], index: number, contentBefore: boolean): HTMLElement => {
  const newParagraph = document.createElement('p');
  nodes.slice(index).forEach((node) => newParagraph.appendChild(node));
  newParagraph.classList.add('ohe');
  newParagraph.style.cssText = block.style.cssText;
  // Hide br tags if they are the first child
  if (newParagraph.firstChild?.nodeName === 'BR') {
    (newParagraph.firstChild as HTMLElement).style.display = 'none';
  }
  block.after(newParagraph);

  return contentBefore ? newParagraph : block;
};

export const hasNoCommentCategories = (feedback: FeedbackData, annotations: Annotation[]): boolean => {
  const commentHelpers = annotations.map((annotation) => annotation.commentHelpers).flat();
  return (feedback == null || feedback.areas.length === 0) && commentHelpers.length === 0;
};
